import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects } from "../../../services/convexService";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, X } from "lucide-react";
import { prepareFileForUpload } from "../../../utils/fileUtils";
import { api } from "../../../../backend/_generated/api";
import { useConvex } from "convex/react";

const AddProject = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createProject } = useConvexProjects();
  const convex = useConvex();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    fundingGoal: "",
    minInvestment: "",
    expectedROI: "",
    projectPurpose: "getInvestment",
    location: "",
    // Category-specific fields
    developmentStage: "",
    problemStatement: "",
    solutionDescription: "",
    technologyDetails: "",
    ipStatus: "",
    marketPotential: "",
    monetizationOptions: "",
    assetsIncluded: [], // Assets included as checkmarks
  });
  const [images, setImages] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    // Skip riskLevel as it's been removed
    if (name === 'riskLevel') {
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setImages(prev => [...prev, ...files]);
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setDocuments(prev => [...prev, ...files]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("You must be logged in to create a project");
      return;
    }

    if (!formData.title || !formData.description || !formData.category ||
      !formData.fundingGoal || !formData.minInvestment || !formData.expectedROI) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      // Upload images to Convex storage
      const imageUrls: string[] = [];
      for (const image of images) {
        const convexFile = await prepareFileForUpload(image);
        const uploadResult = await convex.action(api.fileUpload.uploadFileToStorage, { file: convexFile });

        // Store file metadata in the database
        await convex.mutation(api.files.storeFileMetadata, {
          filename: uploadResult.filename,
          contentType: uploadResult.type,
          size: uploadResult.size,
          url: uploadResult.url,
          storageId: uploadResult.storageId,
          uploadedBy: user.id,
          fileType: "image",
        });

        imageUrls.push(uploadResult.url);
      }

      // Upload documents to Convex storage
      const documentUrls: string[] = [];
      for (const document of documents) {
        const convexFile = await prepareFileForUpload(document);
        const uploadResult = await convex.action(api.fileUpload.uploadFileToStorage, { file: convexFile });

        // Store file metadata in the database
        await convex.mutation(api.files.storeFileMetadata, {
          filename: uploadResult.filename,
          contentType: uploadResult.type,
          size: uploadResult.size,
          url: uploadResult.url,
          storageId: uploadResult.storageId,
          uploadedBy: user.id,
          fileType: "document",
        });

        documentUrls.push(uploadResult.url);
      }

      // Create the project with actual image and document URLs
      const projectData: any = {
        title: formData.title,
        description: formData.description,
        ownerId: user.id,
        category: formData.category,
        fundingGoal: parseFloat(formData.fundingGoal),
        minInvestment: parseFloat(formData.minInvestment),
        expectedROI: parseFloat(formData.expectedROI),
        images: imageUrls,
        documents: documentUrls,
        projectPurpose: formData.projectPurpose as "sell" | "getInvestment",
      };

      // Add category-specific fields
      if (formData.developmentStage) projectData.developmentStage = formData.developmentStage;
      if (formData.problemStatement) projectData.problemStatement = formData.problemStatement;
      if (formData.solutionDescription) projectData.solutionDescription = formData.solutionDescription;
      if (formData.technologyDetails) projectData.technologyDetails = formData.technologyDetails;
      if (formData.ipStatus) projectData.ipStatus = formData.ipStatus;
      if (formData.marketPotential) projectData.marketPotential = formData.marketPotential;
      if (formData.monetizationOptions) projectData.monetizationOptions = formData.monetizationOptions;
      if (formData.assetsIncluded && formData.assetsIncluded.length > 0) projectData.assetsIncluded = formData.assetsIncluded;
      if (formData.location) projectData.location = formData.location;

      const projectId = await createProject(projectData);

      if (projectId) {
        toast.success("Project created successfully! It's pending approval.");
        navigate("/dashboard/project-holder");
      } else {
        toast.error("Failed to create project. Please try again.");
      }
    } catch (error: any) {
      console.error("Error creating project:", error);
      toast.error(error.message || "Failed to create project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">إضافة مشروع جديد</h1>
            <p className="text-muted-foreground">
              قدم مشروعك للحصول على التمويل من المستثمرين
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>معلومات المشروع</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="title">اسم المشروع *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder="أدخل اسم المشروع"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="description">وصف المشروع *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      placeholder="صف مشروعك بالتفصيل"
                      rows={5}
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">الفئة *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="اختر فئة المشروع" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Business">Business / Operating Project</SelectItem>
                        <SelectItem value="Agriculture">Agriculture / Farm</SelectItem>
                        <SelectItem value="Factory">Factory / Industrial</SelectItem>
                        <SelectItem value="Innovation">Innovation / Invention</SelectItem>
                        <SelectItem value="Intellectual Property">Intellectual Property (IP)</SelectItem>
                        <SelectItem value="Prototype">Prototype / R&D Concept</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="location">الموقع</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="أدخل موقع المشروع"
                    />
                  </div>

                  <div>
                    <Label htmlFor="fundingGoal">الهدف المالي (د.ج) *</Label>
                    <Input
                      id="fundingGoal"
                      name="fundingGoal"
                      type="number"
                      value={formData.fundingGoal}
                      onChange={handleChange}
                      required
                      placeholder="أدخل المبلغ المطلوب"
                    />
                  </div>

                  <div>
                    <Label htmlFor="minInvestment">الحد الأدنى للاستثمار (د.ج) *</Label>
                    <Input
                      id="minInvestment"
                      name="minInvestment"
                      type="number"
                      value={formData.minInvestment}
                      onChange={handleChange}
                      required
                      placeholder="أدخل الحد الأدنى للاستثمار"
                    />
                  </div>

                  <div>
                    <Label htmlFor="expectedROI">العائد المتوقع (%) *</Label>
                    <Input
                      id="expectedROI"
                      name="expectedROI"
                      type="number"
                      value={formData.expectedROI}
                      onChange={handleChange}
                      required
                      placeholder="أدخل نسبة العائد المتوقع"
                    />
                  </div>

                  <div>
                    <Label htmlFor="projectPurpose">الغرض من المشروع *</Label>
                    <Select
                      value={formData.projectPurpose}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, projectPurpose: value }))}
                    >
                      <SelectTrigger id="projectPurpose">
                        <SelectValue placeholder="اختر الغرض من المشروع" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="getInvestment">الحصول على استثمار</SelectItem>
                        <SelectItem value="sell">بيع المشروع</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Category-specific fields */}
                  {formData.category === 'Innovation' && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="developmentStage">Development Stage</Label>
                        <Select
                          value={formData.developmentStage}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, developmentStage: value }))}
                        >
                          <SelectTrigger id="developmentStage">
                            <SelectValue placeholder="Select development stage" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Idea">Idea / Concept</SelectItem>
                            <SelectItem value="Prototype">Prototype built</SelectItem>
                            <SelectItem value="MVP">MVP tested</SelectItem>
                            <SelectItem value="Patent Filed">Patent filed</SelectItem>
                            <SelectItem value="Market-ready">Market-ready</SelectItem>
                            <SelectItem value="Generating Revenue">Already generating revenue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="problemStatement">Problem Statement</Label>
                        <Textarea
                          id="problemStatement"
                          name="problemStatement"
                          value={formData.problemStatement}
                          onChange={handleChange}
                          placeholder="What problem exists today?"
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="solutionDescription">Solution Description</Label>
                        <Textarea
                          id="solutionDescription"
                          name="solutionDescription"
                          value={formData.solutionDescription}
                          onChange={handleChange}
                          placeholder="How this invention solves it"
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="technologyDetails">Technology / Design Details</Label>
                        <Textarea
                          id="technologyDetails"
                          name="technologyDetails"
                          value={formData.technologyDetails}
                          onChange={handleChange}
                          placeholder="Technical description, materials, components, etc."
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="ipStatus">Intellectual Property Status</Label>
                        <Select
                          value={formData.ipStatus}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, ipStatus: value }))}
                        >
                          <SelectTrigger id="ipStatus">
                            <SelectValue placeholder="Select IP status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Patent Pending">Patent Pending</SelectItem>
                            <SelectItem value="Patent Filed">Patent Filed</SelectItem>
                            <SelectItem value="Patent Granted">Patent Granted</SelectItem>
                            <SelectItem value="Trademark">Trademark</SelectItem>
                            <SelectItem value="Copyright">Copyright</SelectItem>
                            <SelectItem value="Trade Secret">Trade Secret</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="marketPotential">Market Potential</Label>
                        <Textarea
                          id="marketPotential"
                          name="marketPotential"
                          value={formData.marketPotential}
                          onChange={handleChange}
                          placeholder="Target industry, market size, use cases, competitive advantage"
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="monetizationOptions">Monetization Options</Label>
                        <Textarea
                          id="monetizationOptions"
                          name="monetizationOptions"
                          value={formData.monetizationOptions}
                          onChange={handleChange}
                          placeholder="Sell full rights, License, Partner for production, Royalty-based model"
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Assets Included in the Sale</Label>
                        <div className="space-y-2">
                          {[
                            { id: "designs", label: "Designs/Files" },
                            { id: "prototype", label: "Prototype" },
                            { id: "documentation", label: "Documentation" },
                            { id: "support", label: "Support Period" },
                            { id: "equipment", label: "Equipment" },
                            { id: "intellectualProperty", label: "Intellectual Property" },
                            { id: "other", label: "Other" },
                          ].map((asset) => (
                            <div key={asset.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`asset-${asset.id}`}
                                checked={formData.assetsIncluded.includes(asset.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: [...prev.assetsIncluded, asset.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: prev.assetsIncluded.filter(id => id !== asset.id)
                                    }));
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              <Label htmlFor={`asset-${asset.id}`} className="text-sm font-normal cursor-pointer">
                                {asset.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>صور المشروع</Label>
                    <div className="mt-2">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            انقر لتحميل صور المشروع
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                        />
                      </label>

                      {images.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          {images.map((image, index) => (
                            <div key={index} className="relative">
                              <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <button
                                type="button"
                                className="absolute -top-2 -right-2 w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white"
                                onClick={() => removeImage(index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-center truncate mt-1">{image.name}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>مستندات المشروع</Label>
                    <div className="mt-2">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <FileText className="w-8 h-8 mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            انقر لتحميل مستندات المشروع
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx"
                          multiple
                          onChange={handleDocumentUpload}
                        />
                      </label>

                      {documents.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {documents.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm truncate">{doc.name}</span>
                              </div>
                              <button
                                type="button"
                                className="text-destructive hover:text-destructive/80"
                                onClick={() => removeDocument(index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full bg-hero-gradient hover:opacity-90 text-primary-foreground"
                    disabled={loading}
                  >
                    {loading ? "جارٍ إنشاء المشروع..." : "إنشاء المشروع"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AddProject;